import { useState } from "react";
import { SparkleIcon } from "@phosphor-icons/react";
import { useI18N } from "common/hooks";
import { chatAssistantEnabled } from "common/config";
import {
  VenueDataSourceBadge,
  hasVenueDataSourceBadge,
} from "@/components/VenueCard/VenueDataSourceBadge";
import { VenueDetails } from "common/types";

type AccessibilitySummaryCardProps = {
  venueDetails: VenueDetails;
  /** Opens the assistant docked (desktop/tablet) or full-screen (mobile) —
   *  the caller already knows which, same as the existing "Ask AI" buttons. */
  onOpenAssistant: () => void;
};

/**
 * Top-of-tab card on the Accessibility tab: the AI-written summary and the
 * verified/contributed line, each rendered independently of the other. Moved
 * here from the venue's left-hand column to match the native app's layout —
 * previously this content lived above the Contribute button regardless of
 * which tab was open.
 */
export default function AccessibilitySummaryCard({
  venueDetails,
  onOpenAssistant,
}: AccessibilitySummaryCardProps) {
  const i18n = useI18N();

  const summary = venueDetails?.business?.venue_summary;
  // The provenance line stands on its own: venues awaiting a generated summary
  // still need to say who contributed their data.
  const showDataSource = hasVenueDataSourceBadge(venueDetails);
  if (!summary && !showDataSource) return null;

  return (
    // No border/rounding of its own — the tab panel that hosts this already
    // has one (VenueSheetContent's md:border md:rounded-xl around the whole
    // panel). The summary band bleeds past both the tabpanel's own p-4 and
    // the extra inset the scrollbar-gutter reservation adds on desktop
    // (measured: 32px total on desktop, 16px on mobile) so the background
    // reaches the panel's true edge, flush against its border. md:-mt-4 does
    // the same for the top edge. The re-applied md:px-8 then lands content
    // at the same 32px inset the filter chips and cards below already use,
    // so icons/text line up instead of sitting 16px further left/right.
    <div className="mb-4">
      {/* Summary + assistant CTA — absent until the summary is generated. */}
      {summary && (
        <div className="flex flex-col md:flex-row md:items-start gap-3 bg-surface-secondary -mx-4 px-5 py-6 md:-mx-8 md:px-8 md:py-5 md:-mt-4">
          <div className="flex-1 flex flex-col gap-2 min-w-0">
            <div className="flex items-center gap-2">
              <SparkleIcon
                size={16}
                weight="fill"
                aria-hidden="true"
                className="text-fg-brand-subtle shrink-0"
              />
              <span className="label-m-semibold text-fg-neutral-strong">
                {i18n.get("accessibilitySummary")}
              </span>
            </div>
            {/* Shown in full — cut off at 2 lines previously, unlike the app. */}
            <p className="label-m-medium text-fg-neutral-subtle">{summary}</p>
          </div>

          {/* Same feature flag as "Ask AI" — this is the same assistant, just a
              different entry point, so it can't be visible when that isn't. */}
          {chatAssistantEnabled && (
            <button
              type="button"
              onClick={onOpenAssistant}
              aria-label={i18n.get("askSociabilityBotHint")}
              className="shrink-0 flex items-center justify-center gap-1.5 h-11 md:h-10 px-4 md:pl-3 md:pr-2.5 rounded-lg bg-bg-brand-secondary-default border border-border-token-brand-minimal hover:bg-bg-brand-ghost-hover transition-colors label-m-medium text-fg-neutral-strong"
            >
              {i18n.get("askSociabilityBot")}
              <span className="rounded-full border border-border-token-brand-subtle bg-bg-brand-secondary-default px-2 py-0.5 label-s-medium text-fg-neutral-strong">
                {i18n.get("beta")}
              </span>
            </button>
          )}
        </div>
      )}

      {/* Verified / contributed line — its own card below the summary rather
          than a band attached to it: this is provenance for the whole venue,
          not part of the AI summary, and it shows with or without one.
          Desktop only; on mobile it lives below the Contribute button. */}
      {showDataSource && (
        <div
          className={`hidden md:flex md:items-center px-4 py-3 rounded-lg border border-border-token-brand-minimal bg-bg-brand-secondary-default ${
            summary ? "mt-4" : ""
          }`}
        >
          <VenueDataSourceBadge
            venue={venueDetails}
            iconClassName="w-4 h-4 shrink-0"
            labelClassName="label-s-medium text-fg-neutral-strong"
            withInfo
          />
        </div>
      )}
    </div>
  );
}
